import React, { useCallback, useEffect, useState } from 'react';
import { listSkills, toggleSystemSkill } from '@/services/skillsApi';
import type { SystemSkill } from '@Types/skills';
import { SkillCard } from './SkillCard';
import { SkillsAsyncState } from './SkillsAsyncState';
import {
  ADP_EVENTS,
  getBernoulliAdp,
  trackAdpWaypoint,
} from '@/services/analytics/adp';

interface Props {
  refreshKey?: number;
}

const SystemSkillToggle = ({
  enabled,
  busy,
  onToggle,
}: {
  enabled: boolean;
  busy: boolean;
  onToggle: () => void;
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    onClick={e => {
      e.stopPropagation();
      onToggle();
    }}
    disabled={busy}
    className={`relative inline-flex h-5 w-9 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
      enabled ? 'bg-brand-dark' : 'bg-slate-300'
    }`}>
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
        enabled ? 'translate-x-[18px]' : 'translate-x-0.5'
      }`}
    />
  </button>
);

export const SkillsSystemTab = ({ refreshKey }: Props) => {
  const [systemSkills, setSystemSkills] = useState<SystemSkill[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listSkills();
      setSystemSkills(res.system ?? []);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh, refreshKey]);

  const handleToggle = async (skillId: string, currentEnabled: boolean) => {
    const nextEnabled = !currentEnabled;
    setTogglingId(skillId);
    setSystemSkills(prev =>
      prev.map(s =>
        s.skill_id === skillId ? { ...s, enabled: nextEnabled } : s,
      ),
    );
    try {
      await toggleSystemSkill(skillId, nextEnabled);
      trackAdpWaypoint(getBernoulliAdp(), ADP_EVENTS.SKILLS_SYSTEM_TOGGLE, {
        skill_id: skillId,
        enabled: String(nextEnabled),
      });
    } catch (e) {
      setSystemSkills(prev =>
        prev.map(s =>
          s.skill_id === skillId ? { ...s, enabled: currentEnabled } : s,
        ),
      );
      trackAdpWaypoint(
        getBernoulliAdp(),
        ADP_EVENTS.SKILLS_SYSTEM_TOGGLE_ERROR,
        {
          skill_id: skillId,
        },
      );
      setLoadError(e instanceof Error ? e.message : 'Failed to update skill');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <SkillsAsyncState
      loading={loading}
      error={loadError}
      onRetry={refresh}
      retryLabel="Try again">
      <div className="space-y-4">
        <div>
          <p className="text-xs text-[#768D95]">
            Built-in workflows the assistant uses automatically. Toggle off to
            exclude one from being applied.
          </p>
        </div>
        <div className="space-y-3">
          {systemSkills.map(skill => (
            <SkillCard
              key={skill.skill_id}
              name={skill.name}
              description={skill.description}
              badge={
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
                  System
                </span>
              }
              primaryAction={
                <SystemSkillToggle
                  enabled={skill.enabled}
                  busy={togglingId === skill.skill_id}
                  onToggle={() => handleToggle(skill.skill_id, skill.enabled)}
                />
              }
            />
          ))}
        </div>
      </div>
    </SkillsAsyncState>
  );
};
